import React from "react";
import { Box, Option } from "@mui/joy";
import DarkCard from "components/common/DarkCard";
import DarkSelect from "components/common/DarkSelect";
import IconButtonCTA from "components/common/IconButtonCTA";
import ColorPalette from "components/common/ColorPalette";
import { ExcursionGroupLimitsChips } from "./ExcursionCountWithMaxChip";
import { ParamsFieldsRenderer } from "./ParamsFieldsRenderer";
import { GroupUsersSection } from "./GroupUsersSection";
import { GroupClientsSection } from "./GroupClientsSection";
import { SDelete, SComment } from "styles/StyledMUI";
import { darkBorder, excursionGroupCommentBubbleEmptySoftSx } from "styles/globalStyles";
import { COLOR_PALETTE_GROUP, GROUP_DEFAULT_COLOR } from "utils/colorPalette";
import type { ZekayCustomer } from "api/customers/Customer.typed";
import type { MultiComment } from "api/zekay/comments";
import type { SearchResultItem } from "components/common/SearchInputWithDropdown";
import { templateDisplayName } from "../../../excursions/shared/utils/excursionUtils";
import type {
  ExcursionGroupCardClient,
  ExcursionGroupCardUser,
} from "../../../excursions/shared/utils/excursionPlanningTypes";

export type { ExcursionGroupCardClient, ExcursionGroupCardUser };

export type ExcursionGroupCardGroup = {
  customer_id?: number | null;
  users?: ExcursionGroupCardUser[];
  color?: string | null;
  customer_label?: string;
  customer_last_name?: string;
  customer_first_name?: string;
  customer_qualification?: string;
  customers?: ExcursionGroupCardClient[];
  params?: Record<string, unknown>;
};

const GROUP_COLOR_FALLBACK = GROUP_DEFAULT_COLOR;

function getGroupClients(group: ExcursionGroupCardGroup): ExcursionGroupCardClient[] {
  const fromArray = group.customers;
  if (fromArray !== undefined && fromArray.length > 0) return fromArray;
  if (
    group.customer_id != null &&
    (group.customer_label ?? group.customer_first_name ?? group.customer_last_name)
  ) {
    return [
      {
        id: group.customer_id,
        label: group.customer_label,
        first_name: group.customer_first_name,
        last_name: group.customer_last_name,
        qualification: group.customer_qualification,
        color: group.color ?? undefined,
      },
    ];
  }
  return [];
}

export type ExcursionGroupCardProps = {
  index: number;
  group: ExcursionGroupCardGroup;
  canEdit: boolean;
  userSearchActive: boolean;
  userSearchQuery: string;
  onUserSearchChange: (query: string) => void;
  onUserSearchFocus: () => void;
  onUserSearchClose?: () => void;
  filteredUsers: {
    id: number;
    name: string;
    first_name?: string;
    last_name?: string;
    qualification?: string;
    slotUnavailable?: boolean;
    slotConflictHint?: string;
  }[];
  onSelectUser: (user: {
    id: number;
    name: string;
    first_name?: string;
    last_name?: string;
    qualification?: string;
  }) => void;
  clientSearchActive: boolean;
  clientSearchQuery: string;
  clientSearchResults: ZekayCustomer[];
  onClientSearchChange: (query: string) => void;
  onClientSearchFocus: () => void;
  onClientSearchClose?: () => void;
  onSelectClient: (customer: ZekayCustomer) => void;
  onCreateClientClick?: () => void;
  onRemoveUser: (userIndex: number) => void;
  onRemoveClient: (clientIndex: number) => void;
  onColorChange: (hex: string) => void;
  onUserColorChange?: (userIndex: number, hex: string) => void;
  onClientColorChange?: (clientIndex: number, hex: string) => void;
  onUserCommentsChange?: (userIndex: number, comments: MultiComment[]) => void;
  onClientCommentsChange?: (clientIndex: number, comments: MultiComment[]) => void;
  onCommentsClick: () => void;
  commentsCount: number;
  onRemoveGroup: () => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onClientDragStart?: (e: React.DragEvent, clientIndex: number) => void;
  paramsTemplate?: Record<string, string>;
  onParamsChange?: (params: Record<string, unknown>) => void;
  searchUsersForParams?: (query: string) => Promise<SearchResultItem[]>;
  searchCustomersForParams?: (query: string) => Promise<SearchResultItem[]>;
  searchGuidesForParams?: (query: string) => Promise<SearchResultItem[]>;
  groupTemplates?: { id?: number; name?: string }[];
  selectedGroupTemplateId?: number | null;
  onGroupTemplateChange?: (templateId: number | null) => void;
  onOpenCustomerOverview?: (customerId: number) => void;
  maxUsersPerGroup?: number;
  maxCustomersPerGroup?: number;
  bookingStatusMap?: Record<number, "pending" | "validated">;
};

export default function ExcursionGroupCard({
  index,
  group,
  canEdit,
  userSearchActive,
  userSearchQuery,
  onUserSearchChange,
  onUserSearchFocus,
  onUserSearchClose,
  filteredUsers,
  onSelectUser,
  clientSearchActive,
  clientSearchQuery,
  clientSearchResults,
  onClientSearchChange,
  onClientSearchFocus,
  onClientSearchClose,
  onSelectClient,
  onCreateClientClick,
  onRemoveUser,
  onRemoveClient,
  onColorChange,
  onUserColorChange,
  onClientColorChange,
  onUserCommentsChange,
  onClientCommentsChange,
  onCommentsClick,
  commentsCount,
  onRemoveGroup,
  onDragOver,
  onDrop,
  onClientDragStart,
  paramsTemplate,
  onParamsChange,
  searchUsersForParams,
  searchCustomersForParams,
  searchGuidesForParams,
  groupTemplates,
  selectedGroupTemplateId,
  onGroupTemplateChange,
  onOpenCustomerOverview,
  maxUsersPerGroup,
  maxCustomersPerGroup,
  bookingStatusMap,
}: ExcursionGroupCardProps) {
  const groupColor = group.color ?? GROUP_COLOR_FALLBACK;
  const hasGroupComments = commentsCount > 0;
  const clients = React.useMemo(() => getGroupClients(group), [group]);

  return (
    <DarkCard
      dense
      sx={{
        mb: 1.5,
        borderLeft: `4px solid ${groupColor} !important`,
        backgroundColor: `${groupColor}14`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
          borderBottom: darkBorder,
          pb: 1,
          mb: 1,
        }}
      >
        <GroupUsersSection
          users={group.users ?? []}
          groupColor={groupColor}
          canEdit={canEdit}
          userSearchActive={userSearchActive}
          userSearchQuery={userSearchQuery}
          onUserSearchChange={onUserSearchChange}
          onUserSearchFocus={onUserSearchFocus}
          onUserSearchClose={onUserSearchClose}
          filteredUsers={filteredUsers}
          onSelectUser={onSelectUser}
          onRemoveUser={onRemoveUser}
          onUserColorChange={onUserColorChange}
          onUserCommentsChange={onUserCommentsChange}
          maxUsersPerGroup={maxUsersPerGroup}
          bookingStatusMap={bookingStatusMap}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
          <ExcursionGroupLimitsChips
            leadCount={group.users?.length ?? 0}
            peopleCount={clients.length}
            maxLead={maxUsersPerGroup}
            maxPeople={maxCustomersPerGroup}
            size="sm"
          />
          {groupTemplates && groupTemplates.length >= 1 && canEdit && onGroupTemplateChange && (
            <DarkSelect
              size="sm"
              value={selectedGroupTemplateId ?? ""}
              onChange={(_, v) => onGroupTemplateChange(v != null ? Number(v) : null)}
              sx={{
                minWidth: { xs: 0, sm: 120 },
                "& .MuiSelect-button": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
            >
              {groupTemplates.map((t) => (
                <Option key={t.id} value={t.id}>
                  {templateDisplayName(t)}
                </Option>
              ))}
            </DarkSelect>
          )}
          {canEdit && (
            <ColorPalette
              mode="select"
              value={groupColor}
              onChange={onColorChange}
              size="sm"
              palette={COLOR_PALETTE_GROUP}
              defaultColor={GROUP_DEFAULT_COLOR}
            />
          )}
          <IconButtonCTA
            size="sm"
            variant="soft"
            aria-label="Commentaires"
            ctaVariant={hasGroupComments ? "success" : "accent"}
            onClick={onCommentsClick}
            title="Commentaires"
            sx={hasGroupComments ? undefined : excursionGroupCommentBubbleEmptySoftSx}
          >
            <SComment />
            {hasGroupComments && (
              <Box component="span" sx={{ ml: 0.25, fontSize: "0.75rem" }}>
                {commentsCount}
              </Box>
            )}
          </IconButtonCTA>
          {canEdit && (
            <IconButtonCTA
              size="sm"
              variant="plain"
              aria-label="Supprimer le groupe"
              ctaVariant="danger"
              onClick={onRemoveGroup}
            >
              <SDelete iconColor="red" />
            </IconButtonCTA>
          )}
        </Box>
      </Box>
      <GroupClientsSection
        clients={clients}
        groupColor={groupColor}
        canEdit={canEdit}
        clientSearchActive={clientSearchActive}
        clientSearchQuery={clientSearchQuery}
        clientSearchResults={clientSearchResults}
        onClientSearchChange={onClientSearchChange}
        onClientSearchFocus={onClientSearchFocus}
        onClientSearchClose={onClientSearchClose}
        onSelectClient={onSelectClient}
        onCreateClientClick={onCreateClientClick}
        onRemoveClient={onRemoveClient}
        onClientColorChange={onClientColorChange}
        onClientCommentsChange={onClientCommentsChange}
        onClientDragStart={onClientDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onOpenCustomerOverview={onOpenCustomerOverview}
      />
      {paramsTemplate && Object.keys(paramsTemplate).length > 0 && (
        <Box sx={{ mt: 1.5, pt: 1, borderTop: darkBorder }}>
          <ParamsFieldsRenderer
            paramsTemplate={paramsTemplate}
            params={group.params ?? {}}
            onChange={(p) => onParamsChange?.(p)}
            disabled={!canEdit}
            size="sm"
            labelFontSize="0.75rem"
            gap={1}
            searchUsers={searchUsersForParams}
            searchCustomers={searchCustomersForParams}
            searchGuides={searchGuidesForParams}
          />
        </Box>
      )}
    </DarkCard>
  );
}
